import { NgStyle } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  Output,
  ViewChild,
  inject,
} from '@angular/core';
import { DirectivesModule } from '@freelancer/directives';
import { FreelancerBreakpointValues } from '@freelancer/ui/breakpoints';
import { ButtonComponent } from '@freelancer/ui/button';
import { ButtonColor, ButtonSize } from '@freelancer/ui/button';
import { ContainerComponent } from '@freelancer/ui/container';
import { IconBackdropSize, IconColor, IconSize } from '@freelancer/ui/icon';
import { IconModule } from '@freelancer/ui/icon';
import { StickyBehaviour } from '@freelancer/ui/sticky';
import { ToastAlertContainerComponent } from '@freelancer/ui/toast-alert';
import { ViewHeaderType } from '@freelancer/ui/view-header';
import { CalloutColor } from './callout-color';
import type { CalloutSize } from './callout-size';
import type {
  CalloutContentComponentInterface,
  CalloutPosition,
} from './callout.types';
import { StickyModule } from '../sticky/sticky.module';
import { ViewHeaderComponent } from '../view-header/view-header.component';

// TODO: T267853 - Need animations for fade-in-out.
// Ref: TODO
@Component({
  standalone: true,
  selector: 'fl-callout-content',
  imports: [
    ButtonComponent,
    ContainerComponent,
    DirectivesModule,
    IconModule,
    NgStyle,
    StickyModule,
    ToastAlertContainerComponent,
    ViewHeaderComponent,
  ],
  template: `
    <div
      class="CalloutBody WebappCompatWrapper"
      #calloutBody
      flOutsideClick
      (click)="handleCalloutBodyClick()"
      (mouseenter)="handleCalloutBodyMouseenter()"
      (mouseleave)="handleCalloutBodyMouseleave()"
      (outsideClick)="handleOutsideClick($event)"
      [ngStyle]="{ zIndex }"
      [attr.data-big-border-radius]="bigBorderRadius"
      [attr.data-position]="placement"
      [attr.data-edge-to-edge]="edgeToEdge"
      [attr.data-size]="size"
      [attr.data-color]="color"
      [attr.data-mobile-fullscreen]="mobileFullscreen"
      [attr.data-mobile-close-button]="mobileCloseButton"
      [attr.tabindex]="0"
    >
      @if (shouldShowCloseButton) {
        <fl-icon
          class="CloseButton mobile:hide"
          label="Close"
          i18n-label="Close callout button"
          [name]="'ui-close'"
          [color]="
            color === CalloutColor.LIGHT
              ? IconColor.FOREGROUND
              : IconColor.LIGHT
          "
          [size]="IconSize.SMALL"
          [hoverAnimation]="'spin-and-fade'"
          [clickable]="true"
          (click)="handleCloseButtonClick()"
          [useIconFont]="true"
        />
      }

      @if (mobileFullscreen) {
        <div class="mobile:show">
          <div
            class="CalloutHeader"
            [flSticky]="true"
            [flStickyStatic]="true"
            [flStickyBehaviour]="StickyBehaviour.ALWAYS"
          >
            <fl-view-header
              [type]="ViewHeaderType.CLOSE"
              [heading]="mobileHeaderTitle"
              (close)="handleCloseButtonClick()"
            ></fl-view-header>
            <fl-container>
              <fl-toast-alert-container />
            </fl-container>
          </div>
        </div>
      }
      <div
        class="CalloutContent"
        [class.OverflowContent]="overflowContent"
      >
        <ng-content></ng-content>
      </div>
      @if (!mobileFullscreen && mobileCloseButton) {
        <div class="WrapperCloseButtonMobile mobile:show">
          <fl-button
            i18n="Close callout button"
            [color]="ButtonColor.DEFAULT"
            [size]="ButtonSize.SMALL"
            (click)="handleCloseButtonClick()"
          >
            Close
          </fl-button>
        </div>
      }
    </div>
  `,
  styleUrls: ['./callout-content.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CalloutContentComponent
  implements CalloutContentComponentInterface
{
  ButtonColor = ButtonColor;
  ButtonSize = ButtonSize;
  CalloutColor = CalloutColor;
  IconColor = IconColor;
  IconSize = IconSize;
  StickyBehaviour = StickyBehaviour;
  ViewHeaderType = ViewHeaderType;
  IconBackdropSize = IconBackdropSize;

  zIndex?: number;

  bigBorderRadius: boolean;
  edgeToEdge: boolean;
  hideCloseButton: boolean;
  hover: boolean;
  mobileCloseButton: boolean;
  mobileHeaderTitle: string;
  placement: string;
  size?: CalloutSize;
  overflowContent: boolean;

  isMobile: boolean;
  shouldShowCloseButton: boolean;
  triggerHtmlElement: HTMLElement | undefined;

  @HostBinding('attr.data-color') color: CalloutColor;
  @HostBinding('attr.data-mobile-fullscreen') mobileFullscreen: boolean;

  @Output() bodyClick = new EventEmitter<void>();
  @Output() bodyMouseenter = new EventEmitter<void>();
  @Output() bodyMouseleave = new EventEmitter<void>();
  @Output() close = new EventEmitter<void>();
  @Output() outsideClick = new EventEmitter<void>();

  @ViewChild('calloutBody')
  calloutBody: ElementRef<HTMLDivElement>;

  private changeDetectorRef = inject(ChangeDetectorRef);

  protected handleOutsideClick(event: MouseEvent): void {
    const clickedTrigger = this.triggerHtmlElement?.contains(
      event.target as Node,
    );
    if (clickedTrigger) {
      return;
    }

    this.outsideClick.emit();
  }

  getElementBoundingRect(): DOMRect {
    return this.calloutBody.nativeElement.getBoundingClientRect();
  }

  handleCalloutBodyClick(): void {
    this.bodyClick.emit();
  }

  handleCalloutBodyMouseenter(): void {
    if (!this.isMobile) {
      this.bodyMouseenter.emit();
    }
  }

  handleCalloutBodyMouseleave(): void {
    if (!this.isMobile) {
      this.bodyMouseleave.emit();
    }
  }

  handleCloseButtonClick(): void {
    this.close.emit();
  }

  setOptions(options: {
    bigBorderRadius: boolean;
    color: CalloutColor;
    edgeToEdge: boolean;
    mobileCloseButton: boolean;
    mobileFullscreen: boolean;
    mobileHeaderTitle: string;
    hideCloseButton: boolean;
    hover: boolean;
    overflowContent: boolean;
    size?: CalloutSize;
    triggerHtmlElement: HTMLElement | undefined;
  }): void {
    this.bigBorderRadius = options.bigBorderRadius;
    this.color = options.color;
    this.edgeToEdge = options.edgeToEdge;
    this.hideCloseButton = options.hideCloseButton;
    this.hover = options.hover;
    this.overflowContent = options.overflowContent;
    this.mobileCloseButton = options.mobileCloseButton;
    this.mobileFullscreen = options.mobileFullscreen;
    this.mobileHeaderTitle = options.mobileHeaderTitle;
    this.size = options.size;
    this.triggerHtmlElement = options.triggerHtmlElement;

    this.setMobileOptions();
  }

  setPlacement(placement: CalloutPosition, triggerElement: HTMLElement): void {
    const { anchor } = placement;
    const position = anchor === 'overlayX' ? 'overlayY' : 'overlayX';
    // convert into an `anchor-relative` string for css parsing
    this.placement = `${placement[anchor]}-${placement[position]}`;
    this.changeDetectorRef.markForCheck();
  }

  private setMobileOptions(): void {
    this.isMobile =
      window.innerWidth < parseInt(FreelancerBreakpointValues.TABLET, 10);

    this.shouldShowCloseButton = !this.hideCloseButton && !this.hover;

    // Mobile overrides to standardize dialog UIs
    if (this.isMobile) {
      this.shouldShowCloseButton =
        this.mobileFullscreen || this.shouldShowCloseButton;
    }
  }
}
